import { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
// import torchSVG from './svg/torch.svg';
import cameraSwitchSVG from './svg/camera-switch.svg';

import './styles.scss';
import Portal from './Portal';

const FACING_MODE_USER = 'user';
const FACING_MODE_ENVIRONMENT = 'environment';

type Mime = 'image/jpeg' | 'image/webp' | 'image/png';

interface Image {
  data: string;
  comment?: string;
  width: number;
  height: number;
  mime?: Mime;
}
interface Props {
  facingMode: 'user' | 'environment';
  enableFacingMode: boolean;
  enableMultiShot: boolean;
  screenshotFormat?: Mime;
  onComplete: (images: Image[]) => void;
  onError: (err) => void;
}

const videoConstraints = {
  width: { min: 480, ideal: 1920, max: 4096 },
};

const getImageDimensions = (data: string) => {
  return new Promise(function (resolve) {
    var i = new Image();
    i.onload = () => {
      resolve({ width: i.width, height: i.height });
    };
    i.src = data;
  });
};

const getVideoDevices = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return [];
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'videoinput');
  } catch (err) {
    console.error(err);
    return [];
  }
};

const BlazeCamera = ({
  facingMode: facingModeInitial = FACING_MODE_ENVIRONMENT,
  enableFacingMode = true,
  enableMultiShot = false,
  onComplete,
  onError,
  screenshotFormat = 'image/jpeg',
}: Props) => {
  const snapBtnRef = useRef<HTMLDivElement>(null);
  const webcamRef = useRef<any>(null);
  const activeTrack = useRef<any>(null);
  const [images, setImages] = useState<Image[]>([]);
  const [facingMode, setFacingMode] = useState(facingModeInitial);
  const [videoDevices, setVideoDevices] = useState<any[]>([]);
  const [userMediaError, setUserMediaError] = useState('');

  useEffect(() => {
    getVideoDevices().then(setVideoDevices);
  }, []);

  const animate = () => {
    if (snapBtnRef.current) {
      snapBtnRef.current.className = 'inner-circle pressed';
    }
    setTimeout(() => {
      if (snapBtnRef.current) {
        snapBtnRef.current.className = 'inner-circle unpressed';
      }
    }, 100);
  };

  const capture = () => {
    const ss = webcamRef.current.getScreenshot();
    getImageDimensions(ss).then(({ width, height }) => {
      if (enableMultiShot) {
        setImages([...images, { data: ss, width, height, mime: screenshotFormat }]);
      } else {
        onComplete([{ data: ss, width, height, mime: screenshotFormat }]);
      }
    });
  };

  const handleCameraChange = useCallback(() => {
    setFacingMode((prevState) => (prevState === FACING_MODE_USER ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER));
  }, []);

  const handleFeedTap = (event: any) => {
    if (event.target.tagName.toLowerCase() === 'video') {
      let coords = [event.clientX, event.clientY];
      console.log(coords);
    }
    handleSnapshot();
  };

  const handleSnapshot = () => {
    animate();
    capture();
  };

  const renderCameraChange = () => {
    return;
    if (!enableFacingMode) {
      return;
    }

    if (videoDevices.length < 2) {
      return;
    }

    return <img src={cameraSwitchSVG} onClick={handleCameraChange} />;
  };

  const handleUserMedia = (stream: any) => {
    const tracks = stream.getVideoTracks();

    if (tracks.length) {
      activeTrack.current = tracks[0];
    }

    // TODO handle video recording
    // const imageCapture = new ImageCapture(activeTrack.current);
  };

  const handleUserMediaError = (err) => {
    setUserMediaError(err);
    if (onError) {
      onError(err);
    }
  };

  const handleReviewImages = () => {
    // TODO allow review / delete / comment images
    onComplete(images);
  };

  const renderErrorPage = () => {
    return (
      <div style={{ textAlign: 'center' }}>
        <h3>Unable to access camera</h3>
        <div style={{ marginBottom: '10px' }}>
          <a href="https://support.google.com/chrome/answer/2693767" target="_blank">
            Support for enabling camera access
          </a>
        </div>
        <button style={{ padding: '5px' }} onClick={() => onComplete([])}>
          Back
        </button>
      </div>
    );
  };

  const renderHeader = () => {
    return <div className="close-btn" title="Close" onClick={() => onComplete([])}></div>;
  };

  const renderLastImage = () => {
    if (!images.length) {
      return;
    }

    // render thumb of last image taken
    return (
      <div className="image-thumb" onClick={handleReviewImages}>
        <div className="image-count">{images.length}</div>
        <img src={images[images.length - 1].data} style={{ width: '75px' }} />
      </div>
    );
  };

  const renderSubmitButton = () => {
    if (!images.length) {
      return;
    }

    return (
      <div className="camera-submit-btn" onClick={handleReviewImages}>
        Submit {renderCameraChange()}
      </div>
    );
  };

  if (userMediaError) {
    return (
      <Portal>
        <div className="camera-wrapper">
          <div className="camera-error">{renderErrorPage()}</div>
        </div>
      </Portal>
    );
  }

  return (
    <Portal>
      <div className="camera-wrapper">
        <div className="camera-controls">
          <div className="camera-header">{renderHeader()}</div>
          <div className="camera-void"></div>
          <div className="camera-footer">
            <div>{renderLastImage()}</div>
            <div>
              <div className="snap-btn" onClick={handleSnapshot}>
                <span className="outer-circle"></span>
                <span ref={snapBtnRef} className="inner-circle"></span>
              </div>
            </div>
            <div>{renderSubmitButton()}</div>
          </div>
        </div>

        <div className="camera-feed-wrapper">
          <div>
            <Webcam
              ref={webcamRef}
              screenshotFormat={screenshotFormat}
              forceScreenshotSourceSize
              onClick={handleFeedTap}
              onUserMedia={handleUserMedia}
              onUserMediaError={handleUserMediaError}
              videoConstraints={{ ...videoConstraints, facingMode }}
            />
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default BlazeCamera;
